import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDebouncedValue, useRequestGate } from '../../hooks/useLatestRequest';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const useClientsFunctions = () => {
  useLocale();
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  // Query on the settled value, and let only the newest request's reply
  // reach the screen -- see hooks/useLatestRequest.js.
  const debouncedSearch = useDebouncedValue(search, 300);
  const beginRequest = useRequestGate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 25;

  const STATUS_OPTIONS = ['all', 'active', 'inactive', 'prospect'];
  const STATUS_LABELS  = {
    all:       t('screens.clients.status.all',       { defaultValue: 'All Status' }),
    active:    t('common.status.active',             { defaultValue: 'Active' }),
    inactive:  t('common.status.inactive',           { defaultValue: 'Inactive' }),
    prospect:  t('common.status.prospect',           { defaultValue: 'Prospect' }),
  };
  const TYPE_OPTIONS   = ['all', 'individual', 'company'];
  const TYPE_LABELS    = {
    all:        t('screens.clients.type.all',        { defaultValue: 'All Types' }),
    individual: t('screens.clients.type.individual', { defaultValue: 'Individual' }),
    company:    t('screens.clients.type.company',    { defaultValue: 'Company' }),
  };

  const fetchClients = useCallback(async () => {
    const req = beginRequest();
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter   !== 'all') params.set('type',   typeFilter);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const resp = await axios.get(`${BASE_URL}/clients?${params}`, { withCredentials: true , signal: req.signal });
      if (!req.isCurrent()) return;
      setClients(resp.data?.data || []);
      setTotal(resp.data?.total || 0);
    } catch {
      if (!req.isCurrent()) return;
      setClients([]);
      setTotal(0);
    } finally {
      if (req.isCurrent()) setLoading(false);
    }
  }, [page, statusFilter, typeFilter, debouncedSearch, beginRequest]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleCloseNew = useCallback(() => {
    setShowNew(false);
    setEditingClient(null);
  }, []);
  const handleCreated = useCallback(() => {
    fetchClients();
    setShowNew(false);
    setEditingClient(null);
  }, [fetchClients]);
  const handleEdit = useCallback((client) => {
    setEditingClient(client);
    setShowNew(true);
  }, []);
  const handleImportDone = useCallback(() => {
    fetchClients();
    setShowImport(false);
  }, [fetchClients]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    clients,
    total,
    search, setSearch,
    statusFilter, setStatusFilter,
    typeFilter, setTypeFilter,
    showNew, setShowNew,
    showImport, setShowImport,
    editingClient, setEditingClient,
    page, setPage,
    loading,
    STATUS_OPTIONS, STATUS_LABELS,
    TYPE_OPTIONS, TYPE_LABELS,
    fetchClients,
    handleCloseNew,
    handleCreated,
    handleEdit,
    handleImportDone,
    totalPages,
  };
};

export default useClientsFunctions;
